import { flow } from '@1x2network/flow'
import { BasicLayout, OrientationMap, Orientor, scaleToLongest } from '@1x2network/layout'
import { Component, ContainerSpecification, createSpine, Spine, SpineSpecification } from '@1x2network/pixi_assets'
import gsap from 'gsap'
import { Container, ISize } from 'pixi.js'
import { TransitionPage, TransitionPageSpec } from './TransitionPage'

/**
 * Animation to hide transition between base and bonus game
 * @author Jordan Harrison - <jordan.harrison@1x2network.com>
 * @version 1.0.0
 */
export class Transition extends Component<TransitionSpecification> {
    private spines: OrientationMap<Spine>
    private orientor: Orientor<Spine>
    private logo: Spine
    private layout: BasicLayout
    private page: TransitionPage

    constructor(parent: Container, layout: BasicLayout) {
        super({ spec: 'transition', parent })
        this.layout = layout
        this.visible = true
        this.spines = {
            landscape: createSpine({ spec: this.specification.spine, parent: this }),
            portrait: createSpine({ spec: this.specification.spine, parent: this }),
        }

        // Update renderable state of spines based on orientation (Don't change visible as we want the animations to play)
        this.orientor = new Orientor({
            layout,
            property: this.spines,
            onOrient: (active, inactive) => {
                active.renderable = true
                inactive.renderable = false
            },
        })

        // Position and scale the spine based on the layout size
        this.logo = createSpine({ spec: this.specification.logo, parent: this })
        this.layout.onResize.connect(() => {
            const { size, left, right, top, bottom, orientation } = this.layout
            const centre = { x: (left + right) / 2, y: (top + bottom) / 2 }

            const spine = this.spines[orientation]
            const spineSize = this.specification.size[orientation]
            spine.scale.set(scaleToLongest(spineSize, size))
            spine.position.copyFrom(centre)
        })

        this.page = new TransitionPage({ spec: this.specification.page, layout: this.layout })
        this.addChild(this.page)

        this.visible = false
    }

    /**
     * Play an animation on both spines
     * @param animation Animation key to play, either 'in', 'loop', or 'out'
     * @param loop Whether the animation should loop
     * @returns Promise that resolves when both animations have completed
     */
    private playSpineAnimation(animation: 'in' | 'out' | 'loop', loop: boolean) {
        return Promise.all([
            flow.wait(this.spines.landscape.play(this.specification.animations.landscape[animation], 0, loop)),
            flow.wait(this.spines.portrait.play(this.specification.animations.portrait[animation], 0, loop)),
        ])
    }

    public async enterHoldAndWin() {
        this.page.show()
        const targets = [this.page, this.logo]
        gsap.fromTo(targets, { alpha: 0 }, { alpha: 1, delay: 0.9, duration: 0.5, ease: 'none' })
        await this.transitionIn()
    }

    public exitHoldAndWin() {
        return this.playSpineAnimation('out', false).then(() => {
            this.visible = false
        })
    }

    public transitionIn() {
        // Reset lastTime on both spines since visible is set to false
        this.orientor.call((spine) => spine.reset())
        this.visible = true
        return this.playSpineAnimation('in', false)
    }

    public async transitionOut() {
        const targets = [this.page, this.logo]
        gsap.fromTo(targets, { alpha: 1 }, { alpha: 0, duration: 0.5, ease: 'none' })
        await flow.wait(300)
        return this.playSpineAnimation('out', false).then(() => {
            this.page.hide()
            this.visible = false
        })
    }

    /** display the transition without animating */
    public display() {
        this.page.show()
        this.visible = true
        return this.playSpineAnimation('loop', true)
    }
}

export interface TransitionSpecification extends ContainerSpecification {
    logo: SpineSpecification
    page: TransitionPageSpec
    spine: SpineSpecification
    animations: OrientationMap<{
        in: string
        out: string
        loop: string
    }>
    size: OrientationMap<ISize>
}
